import { whatsNewStateSelector } from '_clean-code/ducks/whatsNew/selectors';
import cn from 'classnames';
import moment from 'moment';
import React, { useRef } from 'react';
import { IoCloseOutline } from 'react-icons/io5';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import gfm from 'remark-gfm';
import styles from './index.module.scss';
import { HashLoader } from 'react-spinners';
import { MdTipsAndUpdates } from 'react-icons/md';
import { Translate } from '@utils/i18n';
import useOutsideClick from '@components/PulsarMenuBar/useOutsideClick';

interface WhatsNewProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const WhatsNew: React.FunctionComponent<WhatsNewProps> = ({
  showModal,
  setShowModal
}) => {
  const { whatsNew, loading } = useSelector(whatsNewStateSelector);
  const ref = useRef(null);
  useOutsideClick(ref, () => {
    setShowModal(false);
  });

  const renderWhatsNew = () => {
    let state;
    if (!whatsNew && loading) {
      state = 'LOADING';
    } else if (whatsNew?.data.length === 0) {
      state = 'NO_UPDATES';
    } else {
      state = 'HAS_UPDATES';
    }

    switch (state) {
      case 'LOADING':
        return (
          <div
            className={styles.fallBackContainer}
            data-testid="loading-spinner"
          >
            <HashLoader color="#4bd1a0" />
          </div>
        );

      case 'NO_UPDATES':
        return (
          <div className={styles.fallBackContainer}>
            <div className={styles.noUpdatesMessage}>
              <p>
                <Translate value="no_new_updates" />
              </p>
              <MdTipsAndUpdates color="#bdbdbd80" size={80} />
            </div>
          </div>
        );

      case 'HAS_UPDATES':
        return (
          <>
            {whatsNew?.data.map((whatsNew) => (
              <React.Fragment key={whatsNew.id}>
                <div className={cn(styles.featureContainer)}>
                  <div className={cn(styles.dateContainer)}>
                    <div className={cn(styles.datePill)}>
                      <span className={cn(styles.dateText)}>
                        {moment(whatsNew.attributes.publishedOn).format(
                          'MMMM D, YYYY'
                        )}
                      </span>
                    </div>
                  </div>
                  <div className={cn(styles.releaseTitleContainer)}>
                    <h1 className={cn(styles.releaseTitleText)}>
                      {whatsNew.attributes.title}
                    </h1>
                  </div>
                  <div className={cn(styles.releaseDescriptionContainer)}>
                    <ReactMarkdown remarkPlugins={[gfm]}>
                      {whatsNew.attributes.releaseNotes}
                    </ReactMarkdown>
                  </div>
                  <img
                    src={
                      whatsNew.attributes.image?.data?.attributes?.formats
                        ?.small?.url
                        ? `${whatsNew.attributes.image.data.attributes.formats.small.url}`
                        : ''
                    }
                    alt="Small"
                  />
                </div>
                <hr className={cn(styles.releaseHr)} />
              </React.Fragment>
            ))}
          </>
        );
    }
  };

  return (
    <div
      className={cn(styles.modalDialog, { [styles.show]: showModal })}
      role="document"
      ref={ref}
    >
      <div className={cn(styles.modalContent)}>
        <div className={styles.modalHeaderContainer}>
          <div className={cn(styles.modalHeader)}>
            <h4 className={cn(styles.modalTitle)} id="myModalLabel">
              <Translate value="whats_new" />
            </h4>
            <div className={styles.closeBtnContainer}>
              <IoCloseOutline
                onClick={() => {
                  setShowModal(false);
                }}
                data-dismiss="modal"
                aria-label="Close"
                size={28}
                color="#464855"
              />
            </div>
          </div>
          <div className={cn(styles.modalSubtextContainer)}>
            <p className={cn(styles.modalSubtext)}>
              <Translate value="whats_new_message" />
            </p>
          </div>
          <hr className={styles.hr} />
        </div>
        <div className={cn(styles.modalBody)}>{renderWhatsNew()}</div>
      </div>
    </div>
  );
};

export default WhatsNew;
